import React, { useState } from 'react';
import styled from 'styled-components';
import useOnclickOutside from 'react-cool-onclickoutside';
import { breakpoints } from '@com/core/common/styles/baseStyles';
import { useTranslation } from '@com/core/common/translations/i18n';
import DialogModal from '@com/core/common/components/Dialog';
import { TAddressesGET } from '@com/core/common/types/customer_address';
import BlockButton from '../common/BlockButton';

type TAddress = TAddressesGET['data'][0];

type TProps = {
    handleEditAddressButton: (address: TAddress) => void;
    handleDeleteAddress: (addressId: string) => void;
    address: TAddress | null;
    isDefault?: boolean;
    onSelectAddress?: (addressId: number) => void;
};

const AddressListItem = ({
    address,
    handleDeleteAddress,
    handleEditAddressButton,
    isDefault,
    onSelectAddress,
}: TProps): JSX.Element => {
    const { t } = useTranslation('common');
    const [modal, setModal] = useState<boolean>(false);
    const [addressChangeModal, setAddressChangeModal] = useState<boolean>(false);
    const [showOptionsMenu, setShowOptionsMenu] = useState<boolean>(false);

    const ref = useOnclickOutside(() => {
        setShowOptionsMenu(false);
    });

    const openModal = (): void => {
        setModal(true);
    };

    const closeModal = (): void => {
        setModal(false);
    };

    const onDeleteClickConfirm = (): void => {
        handleDeleteAddress(address.id.toString());
        closeModal();
    };

    return (
        <AddressListItemContainer key={address.id} className={isDefault ? 'selected' : ''}>
            <button
                className="addressSelectArea"
                type="button"
                disabled={isDefault}
                onClick={() => setAddressChangeModal(true)}>
                <span className={`fauxCheckbox${isDefault ? ' checked' : ''}`} />
                <DetailsContainer>
                    <div className="column">
                        <span className="label">{t('name')}</span>
                        <p className="value">{address ? `${address.firstName || ''} ${address.lastName || ''}` : ''}</p>
                    </div>
                    <div className="column">
                        <span className="label">
                            {t('address')} ({address.type})
                        </span>
                        <p className="value">
                            {address && address.streetAddress && address.area
                                ? `${address.streetAddress}, ${address.area},`
                                : ''}
                            <br />
                            {address && address.city && address.country
                                ? `${address.city.name}, ${address.country.name}`
                                : ''}
                        </p>
                    </div>
                    <div className="column">
                        <span className="label">{t('phone-number')}</span>
                        <p className="value">
                            {address.phone}
                            <span className={`status${!address.isPhoneVerified ? ' warning' : ''}`}>
                                {address.isPhoneVerified ? 'Verified' : 'Not Verified'}
                            </span>
                        </p>
                    </div>
                </DetailsContainer>
            </button>

            {/* MOBILE OPTIONS */}
            <MobileActions>
                <button className="trigger" type="button" onClick={() => setShowOptionsMenu(true)}>
                    <img src="/simpleIcons/more-horizontal.svg" alt="More" />
                </button>
                {showOptionsMenu && (
                    <ul className="menu" ref={ref}>
                        <li>
                            <button
                                type="button"
                                onClick={(): void => handleEditAddressButton(address)}
                                aria-label={t('set-as-default')}>
                                {t('edit')}
                            </button>
                        </li>
                        <li>
                            <button type="button" onClick={openModal} aria-label={t('delete')}>
                                {t('delete')}
                            </button>
                        </li>
                    </ul>
                )}
            </MobileActions>
            {/* DESKTOP OPTIONS */}
            <DesktopActions>
                <div>
                    <BlockButton
                        id={`edit_${address.id.toString()}`}
                        variant="secondary"
                        onClick={(): void => handleEditAddressButton(address)}>
                        {t('edit')}
                    </BlockButton>
                </div>
                <div>
                    <BlockButton id={`delete_${address.id.toString()}`} onClick={openModal} variant="warning">
                        {t('delete')}
                    </BlockButton>
                </div>
            </DesktopActions>
            {modal && (
                <DialogModal
                    onSubmit={onDeleteClickConfirm}
                    onCancel={closeModal}
                    cancelText={t('no')}
                    submitText={t('yes')}>
                    <>
                        <DialogTitle>{t('_address_delete_modal_title')}</DialogTitle>
                        <DialogSubTitle>{t('_address_delete_modal_subtitle')}</DialogSubTitle>
                    </>
                </DialogModal>
            )}

            {addressChangeModal && (
                <DialogModal
                    onSubmit={() => {
                        setAddressChangeModal(false);
                        onSelectAddress(address.id);
                    }}
                    onCancel={() => setAddressChangeModal(false)}
                    cancelText={t('no')}
                    submitText={t('yes')}>
                    <>
                        <p>{t('are_you_sure_you_want_to_change_your_delivery_address')}</p>
                    </>
                </DialogModal>
            )}
        </AddressListItemContainer>
    );
};

const DesktopActions = styled.div`
    visibility: hidden;
    display: none;
    margin: 0 16px;

    @media ${breakpoints.minDesktop} {
        display: flex;
        align-items: center;
    }

    > div + div {
        ${({ theme: { left } }): string => `margin-${left}: 10px`};
    }
`;

const AddressListItemContainer = styled.div`
    display: flex; /* addressSelectArea > actions */

    &:hover,
    &.selected {
        background-color: ${({ theme }): string => theme.colors.brandPrimaryLight};
    }

    + div {
        border-top: 1px solid ${({ theme }): string => theme.colors.borderLight};
    }

    button.addressSelectArea {
        padding: 22px 16px 24px;
        flex: 1;
        display: flex;
        text-align: initial;

        &:hover {
            .fauxCheckbox {
                border-color: ${({ theme }): string => theme.colors.brandPrimary};
            }
        }
    }

    .fauxCheckbox {
        width: 18px;
        height: 18px;
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        border: 1px solid ${({ theme }): string => theme.colors.border};
        border-radius: 100%;
        ${({ theme: { right } }): string => `margin-${right}: 16px`};
        margin-top: 3px;
        position: relative;

        &.checked:after {
            content: '';
            position: absolute;
            top: 3px;
            right: 3px;
            bottom: 3px;
            left: 3px;
            background-color: ${({ theme }): string => theme.colors.brandPrimary};
            border-radius: 100%;
        }
    }

    @media ${breakpoints.minDesktop} {
        :hover > ${DesktopActions} {
            visibility: visible;
        }

        :hover {
            cursor: pointer;
        }
    }
`;

const DetailsContainer = styled.div`
    flex: 1;
    display: flex;
    margin-left: -32px;

    div.column {
        padding-left: 32px;
        font-size: 0.875rem;
        flex: 0 0 48%;

        &:first-child {
            flex: 0 0 27%;
        }

        &:last-child {
            flex: 0 0 25%;
        }
    }

    .label {
        color: ${({ theme }): string => theme.colors.bodyLight};
        display: block;
    }

    .value {
        margin-top: 4px;
    }

    span.status {
        text-transform: uppercase;
        font-weight: 600;
        margin-top: 2px;
        display: block;
        font-size: 0.75rem;
        color: ${({ theme }): string => theme.colors.generalSuccess};

        &.warning {
            color: ${({ theme }): string => theme.colors.generalWarning};
        }
    }
`;

const MobileActions = styled.div`
    position: relative;
    margin-top: 4px;

    @media ${breakpoints.minDesktop} {
        display: none;
    }

    button.trigger {
        padding: 16px;

        img {
            width: 20px;
        }

        &:hover {
            opacity: 0.4;
        }
    }

    ul.menu {
        position: absolute;
        z-index: 2;
        top: auto;
        ${({ theme: { right } }): string => `${right}: 16px`};
        min-width: 145px;
        white-space: nowrap;
        background-color: ${({ theme }): string => theme.colors.bgPrimary};
        border-radius: ${({ theme }): string => theme.metrics.borderRadius};
        border: 1px solid ${({ theme }): string => theme.colors.border};
        box-shadow: 0 0 9px 0 rgba(0, 0, 0, 0.04);
        overflow: hidden;
        margin-top: -8px;

        li + li {
            border-top: 1px solid ${({ theme }): string => theme.colors.border};
        }

        button {
            padding: 12px 16px;
            font-weight: 600;
            width: 100%;
            text-align: initial;

            &:hover {
                background-color: ${({ theme }): string => theme.colors.bgSecondary};
            }
        }
    }
`;

const DialogTitle = styled.h4`
    font-size: 17px;
    text-align: center;
    font-weight: 600;
    margin-bottom: 5px;
`;

const DialogSubTitle = styled.div`
    text-align: center;
`;

export default AddressListItem;
